import { Button, Empty, Flex, Pagination, Skeleton, Space, Typography } from 'antd'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Campaign } from '../../campaign/campaign'
import { TimeAgo } from '../../components/time-ago'
import { useMissedCandidates } from '../hooks'
import { MissedCandidate } from '../missed-candidate'
import { CandidateCreateModal } from './candidate-create-modal'

type MissedCandidatesTableProps = {
    campaignId: Campaign['id']
}

export function MissedCandidatesTable({ campaignId }: MissedCandidatesTableProps) {
    const { t } = useTranslation()
    const [pagination, setPagination] = useState({ page: 1, size: 5 })
    const missedCandidates = useMissedCandidates(campaignId, pagination)
    const [selected, setSelected] = useState<MissedCandidate | null>(null)
    const items = missedCandidates.data?.data ?? []

    return (
        <>
            {missedCandidates.isPending ? (
                <Flex
                    vertical
                    gap="small"
                >
                    <Skeleton active />
                    <Skeleton active />
                </Flex>
            ) : items.length === 0 ? (
                <Empty description={t('No missed candidates')} />
            ) : (
                <Flex
                    vertical
                    gap="small"
                >
                    {items.map((missedCandidate) => {
                        const name =
                            [missedCandidate.firstName, missedCandidate.lastName].filter(Boolean).join(' ') ||
                            t('Unknown candidate')

                        return (
                            <div
                                key={missedCandidate.id}
                                className="rounded-md border border-gray-200 bg-white px-3 py-2"
                            >
                                <Flex
                                    gap="small"
                                    align="center"
                                    justify="space-between"
                                    className="flex-col sm:flex-row"
                                >
                                    <Flex
                                        vertical
                                        gap={4}
                                        className="min-w-0 flex-1"
                                    >
                                        <Flex
                                            align="baseline"
                                            gap="small"
                                            wrap
                                        >
                                            <Typography.Text
                                                strong
                                                className="leading-tight"
                                            >
                                                {name}
                                            </Typography.Text>
                                            <Typography.Text
                                                type="secondary"
                                                className="text-xs"
                                            >
                                                <TimeAgo date={missedCandidate.createdAt} />
                                            </Typography.Text>
                                        </Flex>

                                        <Space
                                            size={6}
                                            wrap
                                        >
                                            {missedCandidate.phone && (
                                                <Typography.Text className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">
                                                    {missedCandidate.phone}
                                                </Typography.Text>
                                            )}
                                            {missedCandidate.email && (
                                                <Typography.Text className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">
                                                    {missedCandidate.email}
                                                </Typography.Text>
                                            )}
                                        </Space>

                                        <Typography.Text
                                            type="secondary"
                                            className="text-xs"
                                        >
                                            {t(`missedCandidate.failure.${missedCandidate.failureType}`)}
                                        </Typography.Text>
                                    </Flex>

                                    <Button
                                        size="small"
                                        onClick={() => setSelected(missedCandidate)}
                                    >
                                        {t('Retry')}
                                    </Button>
                                </Flex>
                            </div>
                        )
                    })}

                    <Pagination
                        align="end"
                        current={missedCandidates.data?.meta.currentPage}
                        defaultPageSize={missedCandidates.data?.meta.perPage}
                        pageSize={missedCandidates.data?.meta.perPage}
                        total={missedCandidates.data?.meta.total}
                        showSizeChanger
                        pageSizeOptions={['5', '10', '20', '50', '100']}
                        onChange={(page, size) => {
                            setPagination({
                                page,
                                size,
                            })
                        }}
                    />
                </Flex>
            )}

            <CandidateCreateModal
                open={!!selected}
                campaignId={campaignId}
                initialValues={{
                    firstName: selected?.firstName ?? undefined,
                    lastName: selected?.lastName ?? undefined,
                    phone: selected?.phone ?? undefined,
                    email: selected?.email ?? undefined,
                }}
                missedCandidateId={selected?.id}
                onSuccess={() => setSelected(null)}
                onCancel={() => setSelected(null)}
            />
        </>
    )
}
